/* eslint-disable camelcase */
import React from 'react';
import TC from 'services/testConnect2p0';
import events from 'lib/events';
import {FormattedMessage, injectIntl, intlShape} from 'react-intl';
import { XFCAbout, XFCInventory } from 'lib/xfConnect';
import { CURRENT_XFCSERVICE_VERSION, xfName } from 'ui/helpers/constants';
import { getErrorCode, ScanningErrors } from 'services/errorCodes';
import {compare_versions} from 'util/version';

class TestConnectStatusBar extends React.Component {
  static propTypes = {
    intl: intlShape.isRequired,
  };

  state = {
    sls_version: '',
    sls_version_outofdate: null,
    noScannersFound: true,
    scanners: [],
    Flex1: null,
    Flex2: null,
    Flex3: null,
    Flex4: null,
    disableXFInventoryChange: true,
  };

  componentDidMount() {
    this.updateStatus();

    this._testConnectErrorListener = events.subscribe('testconnect.hasErrors', () => {
      this.updateStatus();
    });
  }

  componentWillUnmount() {
    this._testConnectErrorListener.remove();
  }

  updateStatus = () => {
    TC.getStatus().then(res => {
      const result = {
        sls_version: res.sls_version,
        sls_version_outofdate: res.sls_version_outofdate,
        noScannersFound: res.scanners.length === 1, // 'Manual' always present in scanners.
        scanners: res.scanners,
      };

      //Get AccuFlex™ Connect Service Info
      XFCAbout()
        .then((xfcStatus) => {
          result.XFCServiceVersion = xfcStatus.details ? xfcStatus.details.softwareVersion : null;
          result.isXFCDeviceConnected = xfcStatus.deviceConnected || false;
          if (result.isXFCDeviceConnected) {

            // Resize history container if showing XF inventory section
            const historyContainerDiv = document.getElementsByClassName('history-container');
            if (historyContainerDiv.length) historyContainerDiv[0].style.height = '250px';

            result.disableXFInventoryChange = false;
            result.noScannersFound = false;
            if (!result.scanners.includes(xfName)) result.scanners.push(xfName);

            // Grab AccuFlex™ Inventory information and update XF lot codes in beaker
            XFCInventory().then((results) => {
              if (results.flexStrips && results.flexStrips.openBottleStripInventory) {
                const newInventoryState = {};
                const inventory = results.flexStrips.openBottleStripInventory;
                for (const [stripName, stripInfo] of Object.entries(inventory)) {
                  if (stripInfo.code) {
                    newInventoryState[stripName] = stripInfo.code;
                  }
                }
                if (Object.keys(newInventoryState).length !== 0) {
                  this.setState(newInventoryState);
                }
              }
            })
              .catch((err) => console.error(err));
            }
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => this.setState(result));
    });
  };


  /**
   * Updates the AccuFlex™ inventory lot codes by sending a request to the XFC service
   *
   */
  updateXFCInventory = () => {
    XFCInventory([this.state.Flex1, this.state.Flex2, this.state.Flex3, this.state.Flex4])
      .then((results) => {
        // XFC returns errors through result codes
        if (results.systemErrorCode !== 0 || results.requestErrorCode !== 0) {
          console.error(results);
          let errorMessage = getErrorCode(ScanningErrors.XFC_INVENTORY_ERROR, xfName) || 'Unexpected Error for Inventory Update';

          // Show XFC system error message if exists
          if (results.systemErrorMsg && results.systemErrorCode) {
            const systemError = this.processXFCErrorMessage(results.systemErrorCode, results.systemErrorMsg);
            errorMessage = <React.Fragment>{errorMessage}{'\n'}{systemError}</React.Fragment>
          }

          // Show XFC request error message if exists
          if (results.requestErrorMsg && results.requestErrorCode) {
            const requestError = this.processXFCErrorMessage(results.requestErrorCode, results.requestErrorMsg, true);
            errorMessage = <React.Fragment>{errorMessage}{'\n'}{requestError}</React.Fragment>
          }

          events.publish('error.user',{
            message: errorMessage
          });

          return;
        }

        // Show success message upon inventory/lot code update
        events.publish('success.user',{
          message: <FormattedMessage id='general.xfc.invsuccess' />
        });
      })
      .catch((err) => {
        console.error(err);
        let errorMessage = getErrorCode(ScanningErrors.XFC_INVENTORY_ERROR, xfName) || 'Unexpected Error for Inventory Update';
        // No error code is thrown in the catch so just display the message if exists
        if (err.message) {
          const systemError = `System Error - "${err.message}"`;
          errorMessage = <React.Fragment>{errorMessage}{'\n'}{systemError}</React.Fragment>;
        }

        events.publish('error.user',{
          message: errorMessage
        });
      })
  };

  /**
   * Processes the XFC error results to set up an appropriate error message
   * with the error code and error message from copy
   * @param {Number} errorCode Error Code value from results
   * @param {string} [errorMessage='Unknown Error'] Error message
   * @param {boolean} [isRequestError=false] Checks if request error or not (i.e., system error)
   * @return {React.Fragment} Processed Error message
   */
  processXFCErrorMessage = (errorCode, errorMessage = 'Unknown Error', isRequestError = false) => {
    let processedErrorMsg = errorMessage;
    if (errorCode) {
      const errorID = `xfc.error.${errorCode}`;
      // Fallback to default error message
      const formattedMessage = <FormattedMessage id={errorID} defaultMessage={errorMessage} />

      let errorHeader;
      if (isRequestError) {
        errorHeader = `Request Error[${errorCode}]`;
        processedErrorMsg = <React.Fragment>{errorHeader}{' - '}{'"'}{formattedMessage}{'"'}</React.Fragment>
      }
      else {
        errorHeader = `System Error[${errorCode}]`;
        processedErrorMsg = <React.Fragment>{errorHeader}{' - '}{'"'}{formattedMessage}{'"'}</React.Fragment>;
      }
    }
    return processedErrorMsg;
  };

  /**
   * Renders the XFC Inventory selection for users to change XF strip lot codes
   *
   */
  renderXFCInventorySection = () => {
    return (
    <div
      className={'row tc-status-bar ' + (this.props.showBorder ? '' : 'no-border')}>
      <div className='col-xs-2' style={{'marginTop': '8px'}}>
        <div className='form-group'>
          <label><FormattedMessage id='general.xfc.flex1' /></label>
          <input type='text' className='form-control' onChange={e => this.setState({ Flex1: e.target.value })}
            defaultValue={this.state.Flex1} />
        </div>
      </div>
      <div className='col-xs-2' style={{'marginTop': '8px'}}>
        <div className='form-group'>
          <label><FormattedMessage id='general.xfc.flex2' /></label>
          <input type='text' className='form-control' onChange={e => this.setState({ Flex2: e.target.value })}
            defaultValue={this.state.Flex2} />
        </div>
      </div>
      <div className='col-xs-2' style={{'marginTop': '8px'}}>
        <div className='form-group'>
          <label><FormattedMessage id='general.xfc.flex3' /></label>
          <input type='text' className='form-control' onChange={e => this.setState({ Flex3: e.target.value })}
            defaultValue={this.state.Flex3} />
        </div>
      </div>
      <div className='col-xs-2' style={{'marginTop': '8px'}}>
        <div className='form-group'>
          <label><FormattedMessage id='general.xfc.flex4' /></label>
          <input type='text' className='form-control' onChange={e => this.setState({ Flex4: e.target.value })}
            defaultValue={this.state.Flex4} />
        </div>
      </div>
      <button className='btn btn-primary pull-right' style={{ 'margin': '16px 8px' }}
          onClick={this.updateXFCInventory}><FormattedMessage id='general.xfc.lotcodes' /></button>
      </div>
    );
  };

  render() {
    const {formatMessage} = this.props.intl;
    // let tcVersionStatus;

    // if (this.state.tc_version) {
    //   tcVersionStatus = !this.state.tc_version_outofdate ?
    //     <p>
    //       <i className='dot green-dot'/><FormattedMessage id='tc-overlay.status.tc-version'/> {this.state.tc_version}
    //     </p> :
    //     <p>
    //       <i className='dot red-dot'/><FormattedMessage id='tc-overlay.status.tc-version'/> {this.state.tc_version}
    //       &nbsp;
    //       <a href="/Content/ALEXProAccuScan2.0.zip"><FormattedMessage id='tc-overlay.status.upgrade'/></a>
    //     </p>;
    // } else {
    //   tcVersionStatus = <p>
    //     <i className='dot red-dot'/><FormattedMessage id='tc-overlay.status.no-conn.tc'/>
    //   </p>;
    // }

    let scannerStatus = this.state.noScannersFound ?
      <p><i className='dot red-dot' /><FormattedMessage id='tc-overlay.status.no-scanners' /></p> :
      <p><i className='dot green-dot' />
        <FormattedMessage id='tc-overlay.status.connected-to.scanners'
          values={{
            scanners: this.state.scanners
              .map(scanner => {
                if (scanner === 'SmartLab' || scanner === 'SmartLab 1') {
                  return 'Smart Lab 1';
                } else if (scanner && (scanner.toString().indexOf('Spin Touch') >= 0 || scanner === 'SmartLab 2')) {
                  return 'Smart Lab 2';
                }
                return scanner;
              })
              .filter(s => s.toLowerCase() !== 'manual')
              .join(` ${formatMessage({id: 'connector.and'})} `),
          }} />
      </p>;

    let serviceVersionStatus;
    let xfcServiceVersionStatus;
    let serviceIcon;
    //TODO (Daniel): Update messaging here to not use defaultMessage.

    // Check Smart Lab Service Status
    if (this.state.sls_version) {
      if (this.state.sls_version_outofdate) {
        serviceVersionStatus = <React.Fragment><FormattedMessage id='tc-overlay.status.sls-version' /> {this.state.sls_version}
          &nbsp;
          <a href='/Content/ALEXProSmartLab1.5.zip'><FormattedMessage id='tc-overlay.status.upgrade' /></a></React.Fragment>;
        serviceIcon = <i className='dot red-dot' />;
      }
      else {
        serviceVersionStatus = <React.Fragment><FormattedMessage id='tc-overlay.status.sls-version' /> {this.state.sls_version}</React.Fragment>
        serviceIcon = <i className='dot green-dot' />;
      }
    }

    // Check XFC Service Status
    if (this.state.XFCServiceVersion && CURRENT_XFCSERVICE_VERSION) {
      if (compare_versions(this.state.XFCServiceVersion, CURRENT_XFCSERVICE_VERSION) >= 0) {
        xfcServiceVersionStatus = <React.Fragment><FormattedMessage id='tc-overlay.status.xfc-version' /> {this.state.XFCServiceVersion}</React.Fragment>;
        serviceIcon = serviceIcon || <i className='dot green-dot' />;
      }
      else {
        xfcServiceVersionStatus = <React.Fragment><FormattedMessage id='tc-overlay.status.xfc-version' /> {this.state.XFCServiceVersion}&nbsp;
          <a href='/Content/ALEXXFConnect1.1.8.3.zip'><FormattedMessage id='tc-overlay.status.upgrade' /></a></React.Fragment>;
        serviceIcon = serviceIcon || <i className='dot red-dot' />;
      }
    }

    // 4 different service text combos depending on status of SLS and XFC services
    if (serviceVersionStatus && xfcServiceVersionStatus) {
      serviceVersionStatus = <p>{serviceIcon}{serviceVersionStatus} {formatMessage({id: 'connector.and'})} {xfcServiceVersionStatus}</p>;
    }
    else if (!serviceVersionStatus && xfcServiceVersionStatus) {
      serviceVersionStatus = <p>{serviceIcon}{xfcServiceVersionStatus}</p>;
    }
    else if (serviceVersionStatus && !xfcServiceVersionStatus) {
      serviceVersionStatus = <p>{serviceIcon}{serviceVersionStatus}</p>
    }
    else {
      serviceVersionStatus = <p>
      <i className='dot red-dot' /><FormattedMessage id='tc-overlay.status.no-conn.all' />
    </p>;
    }

    return (
      <React.Fragment>
        <div
          className={'row tc-status-bar ' + (this.props.showBorder ? '' : 'no-border')} >
          {/*<div className='col-sm-4'>
        {tcVersionStatus}
      </div>*/}
          <div className='col-sm-4'>
            {serviceVersionStatus}
          </div>
          <div className='col-sm-4'>
            {scannerStatus}
          </div>
      </div>
      {!this.state.disableXFInventoryChange && !this.props.hideXFInventory && this.renderXFCInventorySection()}
      </React.Fragment>
    );
  }
}

export default injectIntl(TestConnectStatusBar);
